import { supabase } from '@/lib/supabase-client';

type TenantState = {
    id: string;
    subscriptionStatus: string | null;
    subscriptionCurrentPeriodEnd: string | null;
    stripeSubscriptionId: string | null;
    taxRatePct: number | null;
};

let cachedTenant: TenantState | null = null;
let inFlight: Promise<TenantState> | null = null;

function buildDefaultTenantName(email?: string | null, name?: string | null) {
    const base = name?.trim() || email?.split('@')[0] || 'My Store';
    return `${base}'s Store`;
}

export function clearTenantCache() {
    cachedTenant = null;
}

export async function getActiveTenant(options?: { force?: boolean }) {
    if (options?.force) {
        cachedTenant = null;
        inFlight = null;
    }

    if (cachedTenant) {
        return cachedTenant;
    }

    if (inFlight) {
        return inFlight;
    }

    inFlight = (async (): Promise<TenantState> => {
        const {
            data: { session },
            error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError) {
            throw new Error(sessionError.message);
        }
        const user = session?.user;
        if (!user) {
            throw new Error('You must be signed in to access data.');
        }

        const { data: membership, error: membershipError } = await supabase
            .from('memberships')
            .select('tenant_id')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

        if (membershipError) {
            throw new Error(membershipError.message);
        }

        if (membership?.tenant_id) {
            const { data: tenant, error: tenantError } = await supabase
                .from('tenants')
                .select(
                    'id, subscription_status, subscription_current_period_end, stripe_subscription_id, tax_rate_pct',
                )
                .eq('id', membership.tenant_id)
                .single();

            if (tenantError) {
                throw new Error(tenantError.message);
            }

            cachedTenant = {
                id: tenant.id,
                subscriptionStatus: tenant.subscription_status ?? null,
                subscriptionCurrentPeriodEnd:
                    tenant.subscription_current_period_end ?? null,
                stripeSubscriptionId: tenant.stripe_subscription_id ?? null,
                taxRatePct: (tenant.tax_rate_pct as number | null) ?? null,
            };
            return cachedTenant;
        }

        const tenantName = buildDefaultTenantName(
            user.email,
            (user.user_metadata?.full_name as string | undefined) ?? null,
        );

        const { data: tenant, error: tenantError } = await supabase
            .from('tenants')
            .insert({ name: tenantName })
            .select(
                'id, subscription_status, subscription_current_period_end, stripe_subscription_id, tax_rate_pct',
            )
            .single();

        if (tenantError) {
            throw new Error(tenantError.message);
        }

        const { error: membershipInsertError } = await supabase
            .from('memberships')
            .insert({
                tenant_id: tenant.id,
                user_id: user.id,
                role: 'owner',
            });

        if (membershipInsertError) {
            throw new Error(membershipInsertError.message);
        }

        cachedTenant = {
            id: tenant.id,
            subscriptionStatus: tenant.subscription_status ?? null,
            subscriptionCurrentPeriodEnd:
                tenant.subscription_current_period_end ?? null,
            stripeSubscriptionId: tenant.stripe_subscription_id ?? null,
            taxRatePct: (tenant.tax_rate_pct as number | null) ?? null,
        };
        return cachedTenant;
    })();

    try {
        return await inFlight;
    } finally {
        inFlight = null;
    }
}

export async function getActiveTenantId() {
    const tenant = await getActiveTenant();
    return tenant.id;
}

export function isSubscriptionActive(status?: string | null) {
    return status === 'active' || status === 'trialing';
}

export async function refreshActiveTenant() {
    return getActiveTenant({ force: true });
}

export async function updateTenantTaxRate(
    tenantId: string,
    taxRatePct: number | null,
) {
    const { error } = await supabase
        .from('tenants')
        .update({ tax_rate_pct: taxRatePct })
        .eq('id', tenantId);
    if (error) throw new Error(error.message);
    clearTenantCache();
}
